import {
	getATLContextUrl,
	getDomainInContext,
	getUrlForDomainInContext,
	isFedRamp,
	isIsolatedCloud,
	isolatedCloudDomain,
} from '@atlaskit/atlassian-context';

import { location } from '@townsquare/facade/window';

type AtlassianContextProvidedConfig = {
	fullUrl: string;
	productSmartCardBaseUrl: string;
	homeSmartCardBaseUrl: string;
	classicUrl: string;
	homeUrl: string;
	atlassianAccountUrl: string;
	adminHubUrl: string;
	macUrl: string;
	supportUrl: string;
	gasApiHost: string;
	ffsBaseUrl: string;
};

type ConfigEnvironment = 'local' | 'staging' | 'prod';

type Config = AtlassianContextProvidedConfig &
	typeof stagingConfig & {
		env: Env;
	};

const useProxy = (function () {
	// Inlined by webpack at build time
	if (typeof __USE_PROXY__ !== 'undefined') {
		return __USE_PROXY__;
	}

	// Inlined by Parcel at build time
	if (typeof process.env.__USE_PROXY__ !== 'undefined') {
		return process.env.__USE_PROXY__;
	}

	return false;
})();

const buildDev = (function () {
	// Inlined by webpack at build time
	if (typeof __BUILD_DEV__ !== 'undefined') {
		return __BUILD_DEV__;
	}

	// Inlined by Parcel at build time
	if (typeof process.env.__BUILD_DEV__ !== 'undefined') {
		return process.env.__BUILD_DEV__;
	}

	return false;
})();

export const SITE_IDENTIFIER_PLACEHOLDER = ':siteIdentifier';

/**
 * Atlassian Context URLs. Where @atlaskit/atlassian-context can provide the URL, we should use it.
 * This list _always_ needs to be overrided per environment, so separating for typing purposes.
 * the getUrlForDomainInContext function returns undefined if the domain is not found,
 * so provide a default value to avoid type errors.
 */
const atlassianContextConfig = (env: 'staging' | 'prod'): AtlassianContextProvidedConfig => ({
	fullUrl: `https://${getDomainInContext('home', env) ?? 'id.stg.internal.atlassian.com'}`,
	productSmartCardBaseUrl: `https://${getDomainInContext('team', env) ?? 'team.stg.atlassian.com'}`,
	homeSmartCardBaseUrl: `https://${getDomainInContext('home', env) ?? 'home.stg.atlassian.com'}`,
	classicUrl: `https://${getDomainInContext('team', env) ?? 'team.stg.atlassian.com'}`,
	homeUrl: `https://${getDomainInContext('home', env) ?? 'home.stg.atlassian.com'}`,
	atlassianAccountUrl: `https://${getDomainInContext('id', env) ?? 'id.stg.internal.atlassian.com'}`,
	adminHubUrl: `https://${getDomainInContext('admin', env) ?? 'admin.stg.atlassian.com'}`,
	macUrl: `https://${getDomainInContext('my', env) ?? 'my.stg.atlassian.com'}`,
	supportUrl: `https://${getDomainInContext('support', env) ?? 'support.stg.atlassian.com'}`,
	// This intentionally doesn't include the protocol, it is intended to be a domain only.
	gasApiHost: `${getDomainInContext('home', env) ?? 'home.stg.atlassian.com'}/gateway/api/gasv3/api/v1`,
	ffsBaseUrl: `https://${getDomainInContext('api', env) ?? 'api.stg.atlassian.com'}/flags`,
});

/**
 * Whilst this is defined as "stagingConfig", it is actually the "defaultConfig", in that if
 * any key not overriden will be taken from here in all environments.
 */
const stagingConfig = {
	...atlassianContextConfig('staging'),
	// Non-TC gateway api urls
	atlassianGQLGatewayUrl: '/gateway/api/graphql',
	directoryServiceUrl: '/gateway/api/directory',
	emojiUrl: '/gateway/api/emoji',
	profileCardUrl: '/gateway/api/directory/graphql',
	availableProductsUrl: '/gateway/api/available-products',
	appSwitcherBffUrl: '/gateway/api/app-switcher',
	collabGraphUrl: '/gateway/api/collaboration',
	notificationsUrl: '/gateway/api/notification-log',
	searchUrl: '/gateway/api/xpsearch-aggregator',
	invitationsServiceUrl: '/gateway/api/v3/invitations',
	// TC gateway api urls
	authCheckUrl: '/gateway/api/me',
	orgHasWorkspaceUrl: '/gateway/api/watermelon/organization/containsAnyWorkspaceByHost',
	watermelonUrl: '/gateway/api/watermelon',
	watermelonGraphQLUrl: '/gateway/api/watermelon/graphql',
	townsquareUrl: `/gateway/api/townsquare/s/${SITE_IDENTIFIER_PLACEHOLDER}`,
	townsquareGraphQLUrl: `/gateway/api/townsquare/s/${SITE_IDENTIFIER_PLACEHOLDER}/graphql`,
	aggGraphQLUrl: '/gateway/api/graphql',
	mediaUploadTokenPath: '/media-upload-token',
	mediaRendererTokenPath: '/media-token',
	mediaApiPath: '/media-api',
	reactionsPath: '/reactions',
	userCapabilitiesUrl: '/gateway/api/watermelon/user/capabilities',
	exportProjectCsvByTqlPath: '/export/projects/tql',
	exportGoalCsvByTqlPath: '/export/goals/tql',
	exportTeamCsvByTqlPath: '/export/teams/tql',
	exportPeopleCsvByTqlPath: '/export/users/tql',
	haveISeenItUrl: '/gateway/api/flag/my',
	jiraIntegrationUrl: '/integration/jira',
	msteamsIframeIntegrationUrl: '/integration/msteams',
	// External urls
	wacUrl: 'https://qa-wac.internal.atlassian.com',
	mediaBaseUrl: 'https://media.staging.atl-paas.net',
	learnMoreUrl: 'https://www.atlassian.com/software/team-central',
	adminHubSitePath: '/s',
	adminBillingPath: '/billing',
	billingPreviewPath: '/billing-preview',
	termsOfUseUrl: 'https://www.atlassian.com/legal/cloud-terms-of-service',
	privacyPolicyUrl: 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers',
	internalPrivacyPolicyUrl:
		'https://hello.atlassian.net/wiki/spaces/PMP/pages/139161942/Standard+-+Global+Workplace+Privacy+Notice',
	noticeAtCollectionUrl: 'https://www.atlassian.com/legal/privacy-policy#additional-disclosures-for-ca-residents',
	internalNoticeAtCollectionUrl:
		'https://hello.atlassian.net/wiki/spaces/PMP/pages/139161942/Standard+-+Global+Workplace+Privacy+Notice#13.-Additional-Disclosures-for-California-Residents',
	internalKudosPolicyUrl:
		'https://hello.atlassian.net/wiki/spaces/GDAY/pages/1604155950/Global+-+Kudos+Peer-to-Peer+Recognition',
	awrKudosBlogUrl:
		'https://hello.atlassian.net/wiki/spaces/GDAY/blog/2025/04/22/5192757277/AWR+Employee+Rewards+are+here',
	// Integrations
	msteamsIntegrationUrl: 'http://teamcentral-msteams-integration.stg.atlassian.com',
	slackIntegrationUrl: 'http://teamcentral-slack-integration.stg.atlassian.com',
	milestonesBestPracticesUrl:
		'https://hello.atlassian.net/wiki/spaces/STATUS/pages/2914836294/How+to+best+use+Atlas+milestones+in+projects',
	// Non-FF Feature controls
	enableIntercom: true,
	// Watermelon LD project - Staging
	featureFlagApiKey: 'd814e346-775a-4433-9cc2-bbf04f88c6a0',
	loomCdnUrl: 'https://cdn-stage.loom.com/',
};

type EnvironmentOverrides = { [env in ConfigEnvironment]?: Partial<Config> & AtlassianContextProvidedConfig };

const getLocalOverrides = () => {
	const baseOptions = {
		...atlassianContextConfig('staging'),
		directoryServiceUrl: 'https://home.stg.atlassian.com/gateway/api/directory',
		mediaBaseUrl: 'https://media.staging.atl-paas.net',
		enableIntercom: true,
		msteamsIntegrationUrl: 'http://teamcentral-msteams-integration.stg.atlassian.com',
		// Local development will have to use staging links in order for smart link to resolve properly
		productSmartCardBaseUrl: 'https://team.stg.atlassian.com',
		homeSmartCardBaseUrl: 'https://home.stg.atlassian.com',
	};

	const localBackendUrls = {
		fullUrl: `http://${location().hostname}:${location().port}`,
	};

	return useProxy ? baseOptions : { ...baseOptions, ...localBackendUrls };
};

// TS does not distinguish between missing keys and keys with undefined values.
// Don't specify null/undefined for any config values or the object spread will give you an invalid Config in getConfig()
// https://github.com/Microsoft/TypeScript/issues/13195
const environmentOverrides: EnvironmentOverrides = {
	local: getLocalOverrides(),
	prod: {
		...atlassianContextConfig('prod'),
		mediaBaseUrl: 'https://api.media.atlassian.com',
		wacUrl: 'https://www.atlassian.com',
		msteamsIntegrationUrl: 'http://teamcentral-msteams-integration.atlassian.com',
		slackIntegrationUrl: 'https://teamcentral-slack-integration.atlassian.com',
		termsOfUseUrl: 'https://www.atlassian.com/legal/cloud-terms-of-service',
		privacyPolicyUrl: 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers',
		internalPrivacyPolicyUrl:
			'https://hello.atlassian.net/wiki/spaces/PMP/pages/139161942/Standard+-+Global+Workplace+Privacy+Notice',
		noticeAtCollectionUrl: 'https://www.atlassian.com/legal/privacy-policy#additional-disclosures-for-ca-residents',
		internalNoticeAtCollectionUrl:
			'https://hello.atlassian.net/wiki/spaces/PMP/pages/139161942/Standard+-+Global+Workplace+Privacy+Notice#13.-Additional-Disclosures-for-California-Residents',
		internalKudosPolicyUrl:
			'https://hello.atlassian.net/wiki/spaces/GDAY/pages/1604155950/Global+-+Kudos+Peer-to-Peer+Recognition',
		awrKudosBlogUrl:
			'https://hello.atlassian.net/wiki/spaces/GDAY/blog/2025/04/22/5192757277/AWR+Employee+Rewards+are+here',
		featureFlagApiKey: 'b4a2a380-5620-4085-8e89-80f9566c681c', // Watermelon LD project - Production
		loomCdnUrl: 'https://cdn.loom.com/',
	},
};

const icEnvironmentOverrides: EnvironmentOverrides = {
	staging: {
		...atlassianContextConfig('staging'),
		mediaBaseUrl: `https://media-api.${isolatedCloudDomain() ?? ''}`,
		// macUrl and supportUrl have to be overridden, as their values from atlassianContextConfig
		// will point to the IC base domain, but these URLs won't exist in ICs (and they remain in L2)
		macUrl: 'https://my.stg.atlassian.com',
		supportUrl: 'https://support.stg.atlassian.com/',
		// overriding to home explicitly, as there's no 'team' reserved-domain in IC
		productSmartCardBaseUrl: `https://${getDomainInContext('home', 'prod') ?? ''}`,
		classicUrl: `https://${getDomainInContext('home', 'prod') ?? ''}`,
		// Intercom used by Rovo, which will not be available in Oasis GA, so disabling this
		enableIntercom: false,
	},
	prod: {
		// needed otherwise typescript thinks some URLs are undefined, even with environmentOverrides.prod
		...atlassianContextConfig('prod'),
		...environmentOverrides.prod,
		// see https://developer.atlassian.com/platform/media/getting-started/what-media-urls-should-i-use/#url-mappings
		mediaBaseUrl: `https://media-api.${isolatedCloudDomain() ?? ''}`,
		// macUrl and supportUrl have to be overridden, as their values from atlassianContextConfig
		// will point to the IC base domain, but these URLs won't exist in ICs (and they remain in L2)
		macUrl: 'https://my.atlassian.com',
		supportUrl: 'https://support.atlassian.com/',
		// overriding to home explicitly, as there's no 'team' reserved-domain in IC
		productSmartCardBaseUrl: `https://${getDomainInContext('home', 'prod') ?? ''}`,
		classicUrl: `https://${getDomainInContext('home', 'prod') ?? ''}`,
		// Intercom used by Rovo, which will not be available in Oasis GA, so disabling this
		enableIntercom: false,
	},
};

/**
 * atlassian-context getDomainInContext relies on Cookies to set the env, so we need to use the deprecated
 * getATLContextUrl which gets the env based on the domain to redirect users to "id", where they login and the cookie gets set.
 */
const fedrampEnvironmentOverrides: EnvironmentOverrides = {
	staging: {
		...atlassianContextConfig('staging'),
		...environmentOverrides.staging,
		atlassianAccountUrl: getATLContextUrl('id'),
	},
	prod: {
		...atlassianContextConfig('prod'),
		...environmentOverrides.prod,
		atlassianAccountUrl: getATLContextUrl('id'),
	},
};

export function getEnv(): Env {
	if (buildDev && buildDev !== 'false') {
		return 'local';
	}

	const env = window.__env__;

	if (!env) {
		console.warn('config: No environment override found.');
	}

	return env ?? 'unknown';
}

export function setEnv(env: Env) {
	window.__env__ = env;
}

export function getReleaseVersion(): string {
	if (buildDev && buildDev !== 'false') {
		return 'local';
	}
	const versionMeta: HTMLMetaElement | null = document.querySelector('meta[name="release-version"]');
	if (!versionMeta) {
		return 'noversion';
	} else {
		return versionMeta.content || 'noversion';
	}
}

export function getConfig(): Config {
	const env = getEnv();
	const configEnv = getConfigEnvironment(env);

	if (isIsolatedCloud()) {
		return {
			env,
			...stagingConfig,
			...icEnvironmentOverrides[configEnv],
		};
	}

	// Note - we need to use the domain based version of isFedRamp here.
	if (isFedRamp()) {
		return {
			env,
			...stagingConfig,
			...fedrampEnvironmentOverrides[configEnv],
		};
	}

	return {
		env,
		...stagingConfig,
		...environmentOverrides[configEnv],
	};
}

export function getStaticImagePath(path: string): string {
	return isIsolatedCloud() ? `/bfa/townsquare${path}` : path;
}

export function isInIframe(): boolean {
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
}

export function isEmbedOrIntegration() {
	return /(\/embeds\/)|(\/integration\/)/gi.test(window.location.pathname);
}

export function isIntegration() {
	return /(\/integration\/)/gi.test(window.location.pathname);
}

export function isEmbed() {
	return /(\/embeds\/)/gi.test(window.location.pathname);
}

/**
 * For configuration, we don't need to distinguish between Commercial/IC/FedRamp, only Staging/Production.
 */
const ENV_TO_CONFIG_ENV: Record<Env, ConfigEnvironment> = {
	ddev: 'staging',
	test: 'staging',
	local: 'local',
	'stg-east': 'staging',
	'prod-east': 'prod',
	'stg-fedm-east': 'staging',
	'prod-fedm-east': 'prod',
	unknown: 'prod',
};

const getConfigEnvironment = (env: Env) => ENV_TO_CONFIG_ENV[env] || 'prod';

const ENV_TO_PLATFORM_ENV: Record<Env, 'dev' | 'staging' | 'prod'> = {
	ddev: 'staging',
	test: 'staging',
	local: 'staging',
	'stg-east': 'staging',
	'prod-east': 'prod',
	'stg-fedm-east': 'staging',
	'prod-fedm-east': 'prod',
	unknown: 'prod',
};

/**
 * Get the environment to use for platform services, based on the env the application is running in.
 * For example, when testing locally we will typically use the staging environment for platform services.
 *
 * @returns The environment to use for platform services
 */
export const getPlatformEnvironment = () => ENV_TO_PLATFORM_ENV[getEnv()] || 'prod';

export const getIdentityEnv = (env: Env) => {
	const resolvedEnvironment = ENV_TO_PLATFORM_ENV[env] || 'prod';
	if (isIsolatedCloud()) {
		// Isolated cloud behaviour tells identity the baseUrl
		const urlForDomain = getUrlForDomainInContext('id', resolvedEnvironment);
		if (urlForDomain === undefined) {
			return resolvedEnvironment;
		}
		return {
			baseUrl: urlForDomain,
		};
	}

	// Legacy behaviour outside of Isolated Cloud
	return resolvedEnvironment;
};
